import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { faqCategories, faqPageData } from "@/mocks/faqPageData";

export default function FaqPage() {
  const [activeCategory, setActiveCategory] = useState<string>("General");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const items = faqPageData[activeCategory] || [];

  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "FAQ" },
        ]}
      />

      <PageHero
        badge="Frequently Asked Questions"
        headline="Answers to Common Questions"
        subtext="Everything you need to know about working with AstroJal, our services, process, pricing and support."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />

      {/* Category Tabs */}
      <section className="py-10 md:py-12 bg-background-50 border-b border-background-200/50">
        <div className="container-astro">
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {faqCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenIndex(0);
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white shadow-lg shadow-[#7C3AED]/20"
                    : "bg-background-100 text-foreground-600 border border-background-200/60 hover:border-accent-300/40 hover:text-accent-600"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Accordion */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="container-astro max-w-3xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                {activeCategory} Questions
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="space-y-3">
            {items.map((item, i) => (
              <AnimatedSection key={item.q} delay={i * 80}>
                <div
                  className={`rounded-xl border transition-all duration-300 ${
                    openIndex === i
                      ? "border-accent-300/40 bg-background-50 shadow-lg shadow-accent-500/5"
                      : "border-background-200/60 bg-background-50 hover:border-accent-200/30"
                  }`}
                >
                  <button
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 md:px-6 md:py-5 text-left"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    aria-expanded={openIndex === i}
                  >
                    <span className="text-sm md:text-base font-semibold text-foreground-950 pr-4">
                      {item.q}
                    </span>
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 shrink-0 transition-all duration-300 ${
                        openIndex === i ? "bg-accent-500 text-background-50 rotate-180" : ""
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-lg" />
                    </span>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === i ? "max-h-[500px]" : "max-h-0"
                    }`}
                  >
                    <div className="px-5 pb-5 md:px-6 md:pb-6 text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={200}>
            <div className="mt-12 text-center rounded-2xl bg-background-50 border border-background-200/60 p-8">
              <h3 className="text-lg font-bold text-foreground-950 mb-2 font-heading">
                Still have questions?
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                Can't find the answer you're looking for? Our team is happy to help.
              </p>
              <a
                href="/contact-us"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
              >
                Contact Us
                <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <PageCTA
        heading="Have a Project in Mind?"
        text="Let's discuss how we can build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}